<script setup lang="ts">
const visible = defineModel<boolean>('visible', { default: false })

const { t } = useL10n()
const route = useRoute()
const user = useUserStore()

const CATEGORY_KEYS = ['bug', 'idea', 'other'] as const
type CategoryKey = (typeof CATEGORY_KEYS)[number]

const category = ref<CategoryKey>('idea')
const message = ref('')
const isSending = ref(false)
const sent = ref(false)
const error = ref('')

const categoryOptions = computed(() =>
  CATEGORY_KEYS.map((key) => ({ label: t(`feedback.categories.${key}`), value: key }))
)

watch(visible, (open) => {
  // Same manual scroll lock the other dialogs use — PrimeVue's `block-scroll`
  // lets the page behind the dialog scroll through on mobile.
  document.documentElement.style.overflow = open ? 'hidden' : ''
  document.body.style.overflow = open ? 'hidden' : ''

  if (open) {
    category.value = 'idea'
    message.value = ''
    sent.value = false
    error.value = ''
  }
})

const submit = async () => {
  if (!message.value.trim()) return
  isSending.value = true
  error.value = ''
  try {
    await $fetch('/api/feedback', {
      method: 'POST',
      body: {
        category: t(`feedback.categories.${category.value}`),
        message: message.value.trim(),
        page: route.fullPath,
        userEmail: user.contact?.mail || undefined
      }
    })
    sent.value = true
  } catch {
    error.value = t('feedback.error')
  } finally {
    isSending.value = false
  }
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :draggable="false"
    :header="t('feedback.title')"
    :style="{ width: '460px' }"
    :breakpoints="{ '540px': '92vw' }"
  >
    <div v-if="sent" class="feedback-done">
      <i class="pi pi-check-circle" aria-hidden="true"></i>
      <p>{{ t('feedback.thanks') }}</p>
    </div>
    <div v-else class="feedback-form">
      <p class="feedback-intro">{{ t('feedback.intro') }}</p>
      <div class="feedback-field">
        <label for="feedback-category">{{ t('feedback.category') }}</label>
        <Select
          id="feedback-category"
          v-model="category"
          :options="categoryOptions"
          option-label="label"
          option-value="value"
          fluid
        />
      </div>
      <div class="feedback-field">
        <label for="feedback-message">{{ t('feedback.message') }}</label>
        <Textarea
          id="feedback-message"
          v-model="message"
          rows="4"
          fluid
          :placeholder="t('feedback.placeholder')"
        />
      </div>
      <p v-if="error" class="error">{{ error }}</p>
    </div>

    <template #footer>
      <template v-if="!sent">
        <Button :label="t('global.cancel')" text :disabled="isSending" @click="visible = false" />
        <Button
          :label="t('feedback.send')"
          :disabled="!message.trim()"
          :loading="isSending"
          @click="submit"
        />
      </template>
      <Button v-else :label="t('feedback.close')" @click="visible = false" />
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
.feedback-form {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.feedback-intro {
  margin: 0;
  font-size: 0.8rem;
  color: var(--portal-muted);
}

.feedback-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--portal-text);
  }
}

.feedback-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0;
  text-align: center;

  i {
    font-size: 2rem;
    color: #16a34a;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: var(--portal-text);
  }
}
</style>
